import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTrackingSettings } from '@/hooks/useSiteContentValue';
import { trackPageView, getStoredConsent } from '@/lib/tracking';

// Third-party tracking scripts (GA4/GTM/Meta Pixel) have side effects and
// carry their own global state (window.dataLayer, window.fbq) — imperative
// one-time DOM injection is the right tool here, not JSX metadata tags.
// Mounted once at the app root; each ID injects independently and only
// once it actually has a value (IDs load async from rxk_site_content).
export default function TrackingScripts() {
  const { ga4Id, gtmId, metaPixelId, customHeadCode } = useTrackingSettings();
  const injected = useRef({ ga4: false, gtm: false, pixel: false, custom: false });
  const location = useLocation();
  const isFirstPageView = useRef(true);

  useEffect(() => {
    if (!ga4Id || injected.current.ga4) return;
    injected.current.ga4 = true;

    const loader = document.createElement('script');
    loader.async = true;
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    document.head.appendChild(loader);

    // Google's Consent Mode: default to denied until CookieConsentBanner.tsx
    // records an actual choice (or the visitor already made one on a past
    // visit) — gtag('consent','default',...) must run before gtag('config',
    // ...) for Google to apply it from this page load onward.
    const consent = getStoredConsent() ?? 'denied';
    const inline = document.createElement('script');
    inline.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'${consent}',ad_user_data:'${consent}',ad_personalization:'${consent}',analytics_storage:'${consent}'});gtag('js',new Date());gtag('config','${ga4Id}');`;
    document.head.appendChild(inline);
  }, [ga4Id]);

  useEffect(() => {
    if (!gtmId || injected.current.gtm) return;
    injected.current.gtm = true;

    const script = document.createElement('script');
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(script);

    // Google's own setup instructions require this noscript fallback
    // immediately in <body> (works even with JS disabled) — was missing
    // entirely before, this effect only ever added the head script.
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }, [gtmId]);

  useEffect(() => {
    if (!metaPixelId || injected.current.pixel) return;
    injected.current.pixel = true;

    // Meta's own consent API. 'revoke' holds every Pixel event until consent
    // is granted, and most ad visitors (Facebook/Instagram in-app browsers)
    // never tap the banner, so Meta saw only a fraction of them. rxk has no
    // legal consent requirement (see CookieConsentBanner.tsx), so the Pixel
    // runs by default and only a visitor who taps Decline turns it off.
    const consent = getStoredConsent();
    const script = document.createElement('script');
    script.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${metaPixelId}');fbq('consent','${consent === 'denied' ? 'revoke' : 'grant'}');fbq('track','PageView');`;
    document.head.appendChild(script);
  }, [metaPixelId]);

  useEffect(() => {
    if (!customHeadCode || injected.current.custom) return;
    injected.current.custom = true;

    // Arbitrary pasted-in HTML (verification meta tags, chat widgets, extra
    // pixels) — parsed into a detached document so nothing renders into
    // the page body, then each node is re-created into <head>. A <script>
    // inserted via innerHTML never executes, so it's specifically
    // recreated via createElement (copying src/attributes, or inline
    // text) the same way the fixed integrations above do it.
    const doc = new DOMParser().parseFromString(customHeadCode, 'text/html');
    Array.from(doc.head.childNodes).concat(Array.from(doc.body.childNodes)).forEach((node) => {
      if (node.nodeType === Node.COMMENT_NODE || (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())) return;
      if (node instanceof HTMLScriptElement) {
        const script = document.createElement('script');
        Array.from(node.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
        if (node.textContent) script.textContent = node.textContent;
        document.head.appendChild(script);
      } else if (node instanceof Element) {
        document.head.appendChild(node.cloneNode(true));
      }
    });
  }, [customHeadCode]);

  // SPA route changes never fire the bootstrap scripts' own one-time
  // PageView again — fire one per subsequent navigation ourselves. The
  // very first location is skipped since whichever bootstrap script(s)
  // above already loaded (pixel/GA4/customHeadCode) covered it once each.
  useEffect(() => {
    if (isFirstPageView.current) {
      isFirstPageView.current = false;
      return;
    }
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}
